/**
 * BrandingSettings Component
 * Admin UI for managing global branding configuration
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  getBranding,
  updateBranding,
  resetBranding,
  DEFAULT_BRANDING,
  isValidHexColor,
  hexToRgb
} from '../services/brandingService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebase';
import './BrandingSettings.css';

const BrandingSettings = () => {
  const user = useAuthStore((state) => state.user);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const data = await getBranding();
      setBranding(data);
    } catch (error) {
      console.error('Error loading branding:', error);
      showMessage('error', 'Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleChange = (field, value) => {
    setBranding((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColorChange = (field, value) => {
    // Only update if valid hex or empty
    if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
      handleChange(field, value);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      showMessage('error', 'Logo must be less than 1MB');
      return;
    }

    try {
      setUploadingLogo(true);

      // Upload to Firebase Storage
      const storageRef = ref(storage, `branding/logo-${Date.now()}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      handleChange('logoUrl', downloadUrl);
      showMessage('success', 'Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showMessage('error', 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    handleChange('logoUrl', null);
  };

  const addCcEmail = () => {
    const email = newCcEmail.trim();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    if (branding.emailCcRecipients?.includes(email)) {
      showMessage('error', 'This email is already in the list');
      return;
    }

    handleChange('emailCcRecipients', [...(branding.emailCcRecipients || []), email]);
    setNewCcEmail('');
  };

  const removeCcEmail = (email) => {
    handleChange(
      'emailCcRecipients',
      branding.emailCcRecipients.filter((e) => e !== email)
    );
  };

  const handleSave = async () => {
    // Validate colors
    if (branding.primaryColor && !isValidHexColor(branding.primaryColor)) {
      showMessage('error', 'Primary color must be a valid hex color (e.g., #2962ff)');
      return;
    }
    if (branding.secondaryColor && !isValidHexColor(branding.secondaryColor)) {
      showMessage('error', 'Secondary color must be a valid hex color');
      return;
    }
    if (branding.accentColor && !isValidHexColor(branding.accentColor)) {
      showMessage('error', 'Accent color must be a valid hex color');
      return;
    }

    try {
      setSaving(true);
      await updateBranding(branding, user.uid);
      showMessage('success', 'Branding settings saved successfully!');
    } catch (error) {
      console.error('Error saving branding:', error);
      showMessage('error', error.message || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all branding settings to defaults?')) {
      return;
    }

    try {
      setSaving(true);
      const defaultBranding = await resetBranding(user.uid);
      setBranding(defaultBranding);
      showMessage('success', 'Branding settings reset to defaults');
    } catch (error) {
      console.error('Error resetting branding:', error);
      showMessage('error', 'Failed to reset branding settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading branding settings...</div>;
  }

  return (
    <div className="branding-settings">
      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="branding-layout">
        {/* Form Section */}
        <div className="branding-form">
          <div className="card">
            <h2>Company Information</h2>

            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                className="input"
                value={branding.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="Your Company Name"
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label>Tagline</label>
              <input
                type="text"
                className="input"
                value={branding.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Company tagline or description"
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label>Company Logo</label>
              <div className="logo-upload-area">
                {branding.logoUrl ? (
                  <div className="logo-preview">
                    <img src={branding.logoUrl} alt="Company logo" />
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={removeLogo}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="logo-placeholder">
                    <span>No logo uploaded</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </button>
              </div>
              <p className="help-text">Max 1MB. Recommended: PNG with transparent background, 200x60px</p>
            </div>
          </div>

          <div className="card">
            <h2>Brand Colors</h2>

            <div className="color-grid">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={branding.primaryColor || '#2962ff'}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    className="input color-text"
                    value={branding.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                    placeholder="#2962ff"
                    maxLength={7}
                  />
                </div>
                <p className="help-text">Used for headers, buttons, and accents</p>
              </div>

              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={branding.secondaryColor || '#2d3748'}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    className="input color-text"
                    value={branding.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                    placeholder="#2d3748"
                    maxLength={7}
                  />
                </div>
                <p className="help-text">Used for text and secondary elements</p>
              </div>

              <div className="form-group">
                <label>Accent Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={branding.accentColor || '#e53e3e'}
                    onChange={(e) => handleChange('accentColor', e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    className="input color-text"
                    value={branding.accentColor}
                    onChange={(e) => handleColorChange('accentColor', e.target.value)}
                    placeholder="#e53e3e"
                    maxLength={7}
                  />
                </div>
                <p className="help-text">Used for highlights and important notices</p>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>Invoice & Email Content</h2>

            <div className="form-group">
              <label>Payment Instructions</label>
              <textarea
                className="input"
                rows="3"
                value={branding.paymentInstructions}
                onChange={(e) => handleChange('paymentInstructions', e.target.value)}
                placeholder="e.g., GCash: <account name> <mobile number>"
                maxLength={2000}
              />
              <p className="help-text">Displayed on invoices and payment emails</p>
            </div>

            <div className="form-group">
              <label>Payment Terms</label>
              <input
                type="text"
                className="input"
                value={branding.paymentTerms}
                onChange={(e) => handleChange('paymentTerms', e.target.value)}
                placeholder="Payment is due within 15 days of invoice date"
              />
            </div>

            <div className="form-group">
              <label>Invoice Footer Text</label>
              <input
                type="text"
                className="input"
                value={branding.footerText}
                onChange={(e) => handleChange('footerText', e.target.value)}
                placeholder="Thank you for your business!"
              />
            </div>

            <div className="form-group">
              <label>Email Signature</label>
              <textarea
                className="input"
                rows="3"
                value={branding.emailSignature}
                onChange={(e) => handleChange('emailSignature', e.target.value)}
                placeholder="Best regards,&#10;The Team"
              />
            </div>

            <div className="form-group">
              <label>Invoice Number Prefix</label>
              <input
                type="text"
                className="input"
                value={branding.invoiceNumberPrefix}
                onChange={(e) => handleChange('invoiceNumberPrefix', e.target.value)}
                placeholder="INV"
                maxLength={10}
              />
              <p className="help-text">Prefix for invoice numbers (e.g., INV-2024-001)</p>
            </div>
          </div>

          <div className="card">
            <h2>Email Settings</h2>

            <div className="form-group">
              <label>Email From Name</label>
              <input
                type="text"
                className="input"
                value={branding.emailFromName}
                onChange={(e) => handleChange('emailFromName', e.target.value)}
                placeholder="FusionICT Billing"
              />
              <p className="help-text">Display name for outgoing emails</p>
            </div>

            <div className="form-group">
              <label>CC Recipients</label>
              <p className="help-text">These addresses will be CC'd on all invoice emails</p>

              <div className="cc-recipients-list">
                {branding.emailCcRecipients?.map((email) => (
                  <div key={email} className="cc-recipient-tag">
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={() => removeCcEmail(email)}
                      className="remove-tag"
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="cc-add-row">
                <input
                  type="email"
                  className="input"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  placeholder="email@example.com"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCcEmail())}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addCcEmail}
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          <div className="branding-actions">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Branding Settings'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={saving}
            >
              Reset to Defaults
            </button>
          </div>
        </div>

        {/* Preview Section */}
        <div className="branding-preview">
          <div className="card preview-card">
            <h2>Live Preview</h2>

            <div className="preview-section">
              <h3>Invoice Header</h3>
              <div
                className="preview-invoice-header"
                style={{ borderLeftColor: branding.primaryColor }}
              >
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt="Logo preview"
                    className="preview-logo"
                  />
                ) : (
                  <div
                    className="preview-company-name"
                    style={{ color: branding.primaryColor }}
                  >
                    {branding.companyName || 'Company Name'}
                  </div>
                )}
                <div className="preview-tagline">
                  {branding.tagline || 'Company tagline'}
                </div>
              </div>
            </div>

            <div className="preview-section">
              <h3>Color Palette</h3>
              <div className="preview-colors">
                <div
                  className="preview-color-box"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  <span>Primary</span>
                </div>
                <div
                  className="preview-color-box"
                  style={{ backgroundColor: branding.secondaryColor }}
                >
                  <span>Secondary</span>
                </div>
                <div
                  className="preview-color-box"
                  style={{ backgroundColor: branding.accentColor }}
                >
                  <span>Accent</span>
                </div>
              </div>
            </div>

            <div className="preview-section">
              <h3>Email Button</h3>
              <button
                className="preview-email-button"
                style={{ backgroundColor: branding.primaryColor }}
              >
                Pay Invoice
              </button>
            </div>

            <div className="preview-section">
              <h3>Invoice Footer</h3>
              <div
                className="preview-footer"
                style={{ borderTopColor: branding.primaryColor }}
              >
                <p>{branding.footerText || 'Thank you for your business!'}</p>
                <p className="preview-payment-info">
                  {branding.paymentInstructions || 'Payment instructions'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
